import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Button, Modal, Form } from "react-bootstrap";
import {
  FiBriefcase,
  FiCalendar,
  FiClock,
  FiHelpCircle,
  FiImage,
  FiInfo,
  FiKey,
  FiMapPin,
  FiPhone,
  FiPlusCircle,
} from "react-icons/fi";
import { FaSchool } from "react-icons/fa";
import axiosOrganizationInstance from "./AxiosOrganizationInstance";
import {
  getStoredOrganizationId,
  getStoredUserId,
  setStoredOrganizationId,
} from "./organizationStorage";
import { performLogout } from "../utils/sessionManager";
import { supportUrl } from "../constants/appRoutes";
import { AppSidebarLayout, SidebarAccountMenu } from "../admin/EatsAdminLayout";
import "bootstrap/dist/css/bootstrap.min.css";
import "./css/header.css";
import "./css/dashboard.css";
import {
  getPasswordValidationErrors,
  extractPasswordErrorsFromResponse,
  PasswordErrorsDisplay,
} from "./passwordValidation";

/** Sidebar links per /organization/<section> route; pages read the active tab from ?tab=. */
export const ORGANIZATION_SECTIONS = {
  admin: {
    defaultTab: "Details",
    items: [
      ["Details", FiInfo],
      ["Address", FiMapPin],
      ["Contacts", FiPhone],
      ["Images", FiImage],
    ],
  },
  user: {
    defaultTab: "Today",
    items: [
      ["Today", FiCalendar],
      ["History", FiClock],
    ],
  },
  add: {
    defaultTab: "Create organization",
    items: [["Create organization", FiPlusCircle]],
  },
};

export function getOrganizationTab(section, search) {
  const config = ORGANIZATION_SECTIONS[section];
  const requested = new URLSearchParams(search).get("tab");
  if (config?.items.some(([id]) => id === requested)) return requested;
  return config?.defaultTab || "";
}

function openSupportWindow() {
  const width = 720;
  const height = 720;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;
  const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`;
  const organizationName = localStorage.getItem("organizationName") || "Organization";
  const newWindow = window.open(
    supportUrl({ type: "organization", name: organizationName }),
    "_blank",
    features
  );
  if (!newWindow) {
    alert("Please allow popups for this website");
  }
}

function ChangePasswordModal({ show, onClose, email }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);

  const handleClose = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setError("");
    setPasswordErrors([]);
    onClose();
  };

  const handleUpdatePassword = async () => {
    setError("");
    setPasswordErrors([]);

    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setError("All fields are required!");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("New Password and Confirm Password do not match!");
      return;
    }
    const validationErrors = getPasswordValidationErrors(newPassword);
    if (validationErrors.length > 0) {
      setPasswordErrors(validationErrors);
      return;
    }

    try {
      const token = localStorage.getItem("Token");
      if (!token) {
        setError("User not logged in");
        return;
      }
      await axiosOrganizationInstance.put(
        "/organization/password/change",
        { email, oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Password updated successfully!");
      handleClose();
    } catch (err) {
      console.error("Password change error:", err);
      if (err.response) {
        const apiPasswordErrors = extractPasswordErrorsFromResponse(err.response.data);
        if (apiPasswordErrors.length > 0 && err.response.status === 702) {
          setPasswordErrors(apiPasswordErrors);
        } else {
          setError(err.response.data.message || "Failed to update password");
        }
      } else {
        setError("Something went wrong. Try again!");
      }
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="custom-modal">
      <Modal.Header>
        <Modal.Title>Change Password</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: "50vh", overflowY: "auto", display: "flex", flexDirection: "column" }}
      >
        <PasswordErrorsDisplay errors={passwordErrors} />
        {error && <p style={{ color: "red" }}>{error}</p>}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Enter Old Password</Form.Label>
            <Form.Control
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Enter New Password</Form.Label>
            <Form.Control
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleUpdatePassword}>
          Update Password
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const Organization = ({ isAuthenticated, setIsAuthenticated }) => {
  const location = useLocation();
  const [userDetails, setUserDetails] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [organizationType, setOrganizationType] = useState("");

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("Token");
        const userId = getStoredUserId();
        if (!token || !userId) return;

        const response = await axiosOrganizationInstance.get(`/organization/user/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserDetails({ name: response.data.name, email: response.data.email });
      } catch (error) {
        console.error("Error fetching user details:", error.response?.data || error.message);
      }
    };

    fetchUserDetails();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    const fetchOrganizationLogo = async () => {
      try {
        const token = localStorage.getItem("Token");
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };

        let orgId = getStoredOrganizationId();
        const userId = getStoredUserId();
        if (!orgId && userId) {
          const link = await axiosOrganizationInstance.get(
            `/organization/user/${userId}/organization`,
            { headers }
          );
          if (link.data?.organizationId) {
            orgId = String(link.data.organizationId);
            setStoredOrganizationId(orgId);
          }
        }
        if (!orgId) return;

        const [orgResult, imagesResult] = await Promise.allSettled([
          axiosOrganizationInstance.get(`/organization/${orgId}`, { headers }),
          axiosOrganizationInstance.get(`/organization/${orgId}/images`, { headers }),
        ]);
        if (cancelled) return;
        if (orgResult.status === "fulfilled") {
          setOrganizationType(orgResult.value.data?.organizationType || "");
        }
        if (imagesResult.status !== "fulfilled") {
          setLogoUrl("");
          return;
        }
        const logos = (imagesResult.value.data?.images || []).filter(
          (img) => String(img.imageType || "").toLowerCase() === "logo" && (img.imageUrl || img.imagePath)
        );
        const logo = logos.find((img) => img.isPrimary) || logos[0];
        if (!cancelled) setLogoUrl(logo ? logo.imageUrl || logo.imagePath : "");
      } catch (error) {
        if (!cancelled) setLogoUrl("");
      }
    };

    fetchOrganizationLogo();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    performLogout({ redirectPath: "/partner/login" });
  };

  if (!isAuthenticated) {
    return (
      <div className="organization-portal-outlet-full">
        <Outlet />
      </div>
    );
  }

  const section = location.pathname.split("/")[2] || "";
  const config = ORGANIZATION_SECTIONS[section];
  const activeTab = getOrganizationTab(section, location.search);
  const items = (config?.items || []).map(([id, icon]) => ({
    id,
    label: id,
    icon,
    to: `/organization/${section}?tab=${encodeURIComponent(id)}`,
  }));
  const organizationName = localStorage.getItem("organizationName") || "Organization";

  return (
    <>
      <AppSidebarLayout
        title={organizationName}
        items={items}
        activeId={activeTab}
        ariaLabel="Organization navigation"
        collapsedStorageKey="organizationSidebarCollapsed"
        flushMain
        logo={
          logoUrl ? (
            <img
              src={logoUrl}
              alt={organizationName}
              className="eats-admin-sidebar__logo-img"
              onError={() => setLogoUrl("")}
            />
          ) : organizationType.toLowerCase().includes("school") ? (
            <FaSchool className="eats-admin-sidebar__logo-icon" aria-hidden="true" />
          ) : (
            <FiBriefcase className="eats-admin-sidebar__logo-icon" aria-hidden="true" />
          )
        }
        account={
          <SidebarAccountMenu
            name={userDetails.name || "Organization user"}
            email={userDetails.email || organizationName}
            onSignOut={handleLogout}
          >
            {(close) => (
              <>
                <button
                  type="button"
                  className="maya-account-menu__item"
                  role="menuitem"
                  onClick={() => {
                    close();
                    setShowPasswordModal(true);
                  }}
                >
                  <FiKey aria-hidden="true" />
                  <span>Change Password</span>
                </button>
                <button
                  type="button"
                  className="maya-account-menu__item"
                  role="menuitem"
                  onClick={() => {
                    close();
                    openSupportWindow();
                  }}
                >
                  <FiHelpCircle aria-hidden="true" />
                  <span>Support</span>
                </button>
              </>
            )}
          </SidebarAccountMenu>
        }
      >
        <Outlet />
      </AppSidebarLayout>

      <ChangePasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        email={userDetails.email}
      />
    </>
  );
};

export default Organization;
